import React, { useEffect, useState } from "react";
import { Badge, Button, Card, Flex, Text } from "@radix-ui/themes";
import { useQuery } from "@tanstack/react-query";
import { getQrLocations, getHubRiseConnection, startHubRiseOAuth } from "../../../api/QrOrderingApi";
import { saveOAuthAttempt } from "./oauthSession";
import { useHubRiseConnections } from "./useHubRiseConnections";

import { HubRiseIcon } from "./HubRiseIcon";

export function BrowseHubRiseCard({ userProfile }) {
  const [locationId, setLocationId] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState("");
  const connections = useHubRiseConnections(userProfile);
  const locations = useQuery({
    queryKey: ["hubrise-locations", userProfile.user_id, userProfile.organisation_id],
    enabled: Boolean(userProfile.user_id),
    queryFn: async () => {
      const { data } = await getQrLocations();
      return data;
    },
  });
  useEffect(() => {
    if (locationId || !locations.data || !connections.data) return;
    const saved = connections.data.find((item) => item.is_active) || connections.data[0];
    if (saved && locations.data.some((item) => String(item.id) === String(saved.location_id))) {
      setLocationId(String(saved.location_id));
    } else if (locations.data.length === 1) {
      setLocationId(String(locations.data[0].id));
    }
  }, [locationId, locations.data, connections.data]);
  const connection = useQuery({
    queryKey: ["hubrise-connection", userProfile.user_id, locationId],
    enabled: Boolean(locationId),
    retry: false,
    queryFn: async () => {
      try { return (await getHubRiseConnection(locationId)).data; }
      catch (err) {
        if (err.response?.status === 404) return null;
        throw err;
      }
    },
  });

  const connect = async () => {
    setConnecting(true);
    setError("");
    try {
      const { data } = await startHubRiseOAuth(locationId);
      const url = saveOAuthAttempt(locationId, userProfile.user_id, data);
      window.location.assign(url);
    } catch (err) {
      setError(err.response?.status === 401 ? "Your session has expired. Sign in again before connecting." : err.response?.data?.detail || err.message || "Could not start the HubRise connection.");
      setConnecting(false);
    }
  };

  return (
    <Card style={{ width: "100%", padding: "var(--space-4)" }}>
      <Flex direction="column" gap="3">
        <Flex justify="between" align="center">
          <Flex gap="3" align="center">
            <HubRiseIcon />
            <Flex direction="column">
            <Text size="4" weight="medium">HubRise</Text>
            <Text size="2" color="gray">Connect your restaurant menu and orders.</Text>
            </Flex>
          </Flex>
          {connection.data?.is_active && <Badge color="green">Connected</Badge>}
        </Flex>
        <label>
          <Text as="span" size="2">B-Platform location</Text>
          <select aria-label="B-Platform location" value={locationId} disabled={connecting || locations.isLoading}
            onChange={(event) => { setLocationId(event.target.value); setError(""); }}
            style={{ display: "block", width: "100%", padding: 10, marginTop: 6, borderRadius: 6, border: "1px solid var(--gray-a6)", background: "var(--color-background)", color: "var(--gray-12)" }}>
            <option value="">Choose a location</option>
            {(locations.data || []).map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
          </select>
        </label>
        {locations.isError && <Text color="red" size="2">Could not load your locations. Refresh to try again.</Text>}
        {connections.isError && <Text color="red" size="2">Could not load saved HubRise connections. Select a location to check it.</Text>}
        {locations.data?.length === 0 && <Text size="2" color="gray">Create or request access to a B-Platform location first.</Text>}
        {connection.isError && <Text color="red" size="2">Could not load the connection status.</Text>}
        {connection.data && <Text size="2" color="gray">
          HubRise location: {connection.data.hubrise_location_name || connection.data.hubrise_location_id}
          <br />Catalog: {connection.data.catalog_name || connection.data.catalog_id || "Not selected"}
        </Text>}
        {error && <Text role="alert" color="red" size="2">{error}</Text>}
        <Button disabled={!locationId || connecting || connection.isFetching || connection.isError || Boolean(connection.data?.is_active && !connection.data?.reconnect_required)} loading={connecting} onClick={connect}>
          {connection.data?.is_active ? "Reconnect HubRise" : "Connect HubRise"}
        </Button>
      </Flex>
    </Card>
  );
}
